"use client";

import { useState } from "react";
import { cn } from "@repo/ui/utils.ts";
import { ColorBlock } from "@repo/ui/color-block";
import { Actions } from "@/components/common/actions/actions.component";
import { RichTextBodyCopyWithLinks } from "@/components/common/rich-text/rich-text-body-copy-with-links.component";
import type { CTAListProps } from "./cta-list.types";

/**
 * CTAList Component
 *
 * A presentational call-to-action component with a headline, navigation-style
 * list of clickable items on the left, and body content on the right that updates
 * based on the selected item. Uses ColorBlock for consistent styling
 * with other CTA components.
 *
 * @example
 * ```tsx
 * <CTAList
 *   headline="Get in touch with us"
 *   items={[
 *     { key: "1", headline: "Marina Yacht Office", body: richTextData },
 *     { key: "2", headline: "Immigrations Office", body: richTextData },
 *   ]}
 *   theme="onDark"
 * />
 * ```
 */
export function CTAList({
  headline,
  items,
  actions,
  theme = "onLight",
  bannerColour,
  className,
}: CTAListProps) {
  const isDark = theme === "onDark";
  const resolvedColour = bannerColour ?? (isDark ? "cobalt" : "bright-blue");
  const [selectedIndex, setSelectedIndex] = useState(0);

  const buttonVariants = isDark
    ? (["white-filled", "white-outline"] as const)
    : (["blue-filled", "blue-outline"] as const);

  // Get the selected item's body content to display on the right
  const selectedItemBody = items[selectedIndex]?.body;

  return (
    <ColorBlock
      variant="rectangle-sm"
      color={resolvedColour}
      className={cn(isDark && "dark", "text-text-primary", className)}
      data-testid="cta-list"
    >
      <div className="container-grid py-10 tablet:py-24">
        <div className="col-span-full grid grid-cols-subgrid gap-y-8 tablet:gap-y-12 desktop:gap-y-16">
          <div className="col-span-full tablet:col-span-5 flex flex-col">
            {headline && (
              <h2 className="typography-mobile-heading-h4 tablet:typography-desktop-heading-h4 mb-10 text-text-primary">
                {headline}
              </h2>
            )}

            {items.length > 0 && (
              <ul className="space-y-3 tablet:space-y-4 mb-8 tablet:mb-0">
                {items.map((item, index) => (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => setSelectedIndex(index)}
                      className={cn(
                        "flex items-start gap-3 text-left cursor-pointer",
                        "typography-body-sm-regular tablet:typography-body-md-regular",
                        "text-text-primary",
                        selectedIndex === index && "font-medium",
                      )}
                    >
                      <span
                        className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-text-primary"
                        aria-hidden="true"
                      />
                      <span>{item.headline}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <Actions
              actions={actions}
              buttonVariants={[...buttonVariants]}
              className="mt-auto tablet:hidden"
            />
          </div>

          {selectedItemBody && (
            <RichTextBodyCopyWithLinks
              richText={selectedItemBody}
              as="cta"
              className="text-text-primary hidden tablet:block col-span-full tablet:col-span-4 tablet:col-start-8 desktop:col-start-8 desktop:col-span-4"
            />
          )}
        </div>

        {selectedItemBody && (
          <RichTextBodyCopyWithLinks
            richText={selectedItemBody}
            as="cta"
            className="text-text-primary col-span-full mt-8 tablet:hidden"
          />
        )}

        {/* Actions - shown on desktop in right column area */}
        <Actions
          actions={actions}
          buttonVariants={[...buttonVariants]}
          className="hidden tablet:flex col-span-full tablet:col-span-4 tablet:col-start-8 mt-4"
        />
      </div>
    </ColorBlock>
  );
}
